import { DeepPartial, DeepReadonly } from 'ts-essentials';
import {
    ITimingData, IEGTimeNodeChoice, ITimingProps, STTLTimeNodeType, UnitTimeNode, IEGTimeNodeChoiceDelta,
    K_DEFAULT_TRIGGER_ID, MEDIA_TRIGGER_ID_PREFIX,
    DEFAULT_CLICK_SPID, AnimType,
} from '@tencent/slide-sdk-model';
import { ITimingModel } from '@src/slide/domain/ui-model/element-modeles/Interface/timing-model.interface';
import AnimationModel from '@src/slide/domain/ui-model/element-modeles/anim-model';
import { IObservableArray, transaction } from 'mobx';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import {
    IElementBaseModel,
} from '@src/slide/domain/ui-model/element-modeles/Interface/base/element-base-model.interface';
import { AnimListItem, animListToAnimMap } from '@src/slide/domain/ui-model/adapter/anim-handler';
import flatten from 'lodash/flatten';
import { IAnimListItem, IAnimModel } from '@src/slide/domain/ui-model/element-modeles/Interface/anim-model.interface';
import { PresetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constants';
import { presetClassTypes } from '@src/slide/domain/ui-model/element-modeles/property/animation/constant';

export class TimingPropertyConverter {
    /**
     * 数据3.0转换接口
     * @param sdkData
     * @param uiData
     */
    public static convertPartialDataFromSdkToUi(
        sdkData: DeepReadonly<DeepPartial<ITimingProps>>,
        uiData: ITimingModel,
    ): void {
        if (!sdkData.unitTimeNodeMap) return;
        const slideModel = uiData.parent as ISlideModel;

        const animList: Array<IAnimListItem> = [];
        const animTargetIdSet: Set<string> = new Set();
        const { unitTimeNodeMap } = sdkData;
        const animMapNeedToRemove = animListToAnimMap(slideModel.animList);
        const mediaAnimKeyList: string[] = [];
        Object.keys(unitTimeNodeMap).forEach((unitKey: string) => {
            const item = unitTimeNodeMap[unitKey];
            if (!item || item.type === undefined) return;
            if (unitKey.startsWith(MEDIA_TRIGGER_ID_PREFIX)) {
                mediaAnimKeyList.push(unitKey);
                return;
            }
            const uiAnimGroup: Array<Array<AnimationModel>> = [];
            const uiAnimArray: Array<AnimationModel> = [];
            const timeNodeList = item.unitTimeNodeList;
            const oldAnimList = animMapNeedToRemove[unitKey === K_DEFAULT_TRIGGER_ID ? DEFAULT_CLICK_SPID : unitKey];
            timeNodeList?.forEach((timeNodeChoice: IEGTimeNodeChoice, index) => {
                const timeNode = new UnitTimeNode(timeNodeChoice);
                const cTn = timeNode.getData().par?.cTn;
                if (!cTn) return;
                const oldUiModel = oldAnimList?.data.find((animModel: AnimationModel, index) => {
                    if ((animModel.sdkData as IEGTimeNodeChoiceDelta)?.par?.cTn?.id === cTn.id) {
                        oldAnimList.data.splice(index, 1);
                        return true;
                    }
                    return false;
                });
                // 对于已经存在的动画，无需每次有变更时new，直接使用原有的aminationModel即可
                // 原有aminationModel保存有选中态等数据
                const uiModel = oldUiModel ?? new AnimationModel();
                uiAnimArray.push(uiModel);

                uiModel.spid = timeNode.shapeId;
                animTargetIdSet.add(uiModel.spid);
                uiModel.parent = uiData.parent;
                uiModel.selectionService = uiModel.parent?.selectionService;
                uiModel.order = uiAnimGroup.length;
                uiModel.bindSDK(timeNodeChoice);
                if (timeNode.nodeType === STTLTimeNodeType.clickEffect || index === timeNodeList.length - 1) {
                    if (timeNode.nodeType === STTLTimeNodeType.clickEffect) {
                        uiModel.order = uiAnimGroup.length + 1;
                    }
                    uiAnimGroup.push(uiAnimArray.splice(0, uiAnimArray.length));
                }
                uiModel.playOrder = uiModel.order;
                uiData.parent?.children.find(child => (
                    child.id === uiModel.spid ? uiModel.elementModel = child : false
                ));
                AnimationModel.convertDataSdkToUi(cTn, uiModel, uiModel);
            });
            const animListItem: AnimListItem = new AnimListItem({
                sequenceTypes: STTLTimeNodeType[item.type],
                data: flatten(uiAnimGroup),
                clickSpid: unitKey === K_DEFAULT_TRIGGER_ID ? DEFAULT_CLICK_SPID : unitKey,
            });
            if (animListItem.clickSpid === DEFAULT_CLICK_SPID) {
                animList.unshift(animListItem);
            } else {
                animList.push(animListItem);
            }
        });
        // 将媒体队列里的动画数据更新到关联的主队列动画model上
        mediaAnimKeyList.forEach((mediaKey) => {
            const mediaAnimNode = unitTimeNodeMap[mediaKey];
            // 默认 media 各类 interactive seq 的动画参数修改/读取的都是第一个（index 为 0）
            const audioNumberedSlide = mediaAnimNode?.unitTimeNodeList?.[0]?.audio?.cMediaNode?.numSld;
            const audioRepeatCount = mediaAnimNode?.unitTimeNodeList?.[0]?.audio?.cMediaNode?.cTn?.repeatCount;

            const spid = mediaKey.replace(MEDIA_TRIGGER_ID_PREFIX, '');
            const targetAnimModelArr = TimingPropertyConverter.findTargetAnimModelBySpidAndPresetClass(
                animList,
                spid,
                PresetClassTypes.MEDIA,
            );
            if (targetAnimModelArr?.length) {
                // audioNumberedSlide 和 audioRepeatCount 属于播放类动画拥有的属性
                // 所有 targetAnimMode 共用 audioNumberedSlide 和 audioRepeatCount
                targetAnimModelArr.forEach((targetAnimModel: IAnimModel) => {
                    targetAnimModel.props.animType === AnimType.Play
                    && Object.assign(targetAnimModel, { audioNumberedSlide, audioRepeatCount });
                });
            }
        });

        // 将animList里的每个关联到对应的元素Model上
        transaction(() => {
            slideModel.animList.clear() && slideModel.animList.push(...animList);
            // 更新各个 shape 上的 animList
            slideModel.children.forEach((element: IElementBaseModel) => {
                (element.animList as IObservableArray<AnimationModel>).clear();
                slideModel.animList.forEach((animListItem: AnimListItem) => {
                    animListItem.data?.forEach((animModel: AnimationModel) => {
                        animModel.spid === element.id && element.animList?.push(animModel);
                    });
                });
            });
            // @ts-ignore
            uiData.children = slideModel.animList;
        });
        uiData.animTargetIdSet = animTargetIdSet;
    }

    public static checkHasAnim(sdkData: DeepReadonly<DeepPartial<ITimingData>>): boolean {
        if (!sdkData?.unitTimeNodeMap) return false;
        return !!Object.entries(sdkData?.unitTimeNodeMap).find(([, item]) => item?.unitTimeNodeList?.length);
    }

    // eslint-disable-next-line complexity
    public static checkHasAnimation(
        modelId?: string,
        timingModel?: ITimingModel,
        timing?: DeepReadonly<DeepPartial<ITimingData>>,
    ): boolean {
        if (!modelId) return false;
        if (timingModel?.animTargetIdSet?.size) {
            return timingModel?.animTargetIdSet.has(modelId);
        }
        if (!timing) return false;

        const { unitTimeNodeMap } = timing;
        if (!unitTimeNodeMap) return false;
        const keys = Object.keys(unitTimeNodeMap);
        for (let i = 0; i < keys.length; i++) {
            const unitKey = keys[i];
            const item = unitTimeNodeMap[unitKey];
            if (!item) return false;
            const timeNodeList = item.unitTimeNodeList;
            if (!timeNodeList) return false;
            for (let j = 0; j < timeNodeList.length; j++) {
                const timeNodeChoice = timeNodeList[j];
                const timeNode = timeNodeChoice?.par?.cTn;
                if (!timeNode) break;
                const timeNodeChoiceArray = timeNode.childTnLst?.egTimeNodeChoice;
                for (let k = 0; timeNodeChoiceArray && k < timeNodeChoiceArray.length; k++) {
                    const childTimeNodeChoice = timeNodeChoiceArray[k];
                    if (!childTimeNodeChoice) continue;
                    for (const childTimeNodeChoiceKey in childTimeNodeChoice) {
                        if (!childTimeNodeChoice[childTimeNodeChoiceKey]) continue;
                        const spid = childTimeNodeChoice[childTimeNodeChoiceKey]?.cBhvr?.tgtEl?.spTgt?.spid;
                        if (spid === modelId) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }

    public static getSdkTriggerId(id: string) {
        if (id === 'default_click_spid') return K_DEFAULT_TRIGGER_ID;
        return id;
    }

    public static getNumSld(animList: Array<IAnimModel>) {
        let audioNumSld = 0;
        animList.some((animModel) => {
            const { presetClass } = animModel.props;
            const { audioNumberedSlide = 0 } = animModel;
            // 演示态、媒体类型动画、需要停止播放的页数大于1的情况，保留音频
            if (presetClass === presetClassTypes.MEDIA && audioNumberedSlide > 1) {
                audioNumSld = audioNumberedSlide;
                return true;
            }
            return false;
        });
        return audioNumSld;
    }

    private static findTargetAnimModelBySpid(animList: Array<IAnimListItem>, spid: string): IAnimModel|undefined {
        // eslint-disable-next-line no-restricted-syntax
        for (const animListItem of animList) {
            if (!animListItem) {
                continue;
            }
            // eslint-disable-next-line no-restricted-syntax
            for (const animModel of animListItem.data) {
                if (!animModel) {
                    continue;
                }
                if (animModel.spid === spid) {
                    return animModel;
                }
            }
        }
    }

    private static findTargetAnimModelBySpidAndPresetClass(
        animList: Array<IAnimListItem>,
        spid: string,
        presetClass: PresetClassTypes,
    ): IAnimModel[] {
        const animModelArr: IAnimModel[] = [];
        // eslint-disable-next-line no-restricted-syntax
        for (const animListItem of animList) {
            if (!animListItem) {
                continue;
            }
            // eslint-disable-next-line no-restricted-syntax
            for (const animModel of animListItem.data) {
                if (!animModel) {
                    continue;
                }
                if (animModel.spid === spid && animModel.props.presetClass === presetClass) {
                    animModelArr.push(animModel);
                }
            }
        }
        return animModelArr;
    }
}
